import { App } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomModal,
  CustomSelect,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import { useQueryGetCustomerById } from "src/queries/customers.queries";
import { useQueryGetDistributorList } from "src/queries/distributor.queries";
import { useQueryGetClientLookup } from "src/queries/lookup.queries";
import { useMutationMoveVehicles } from "src/queries/vehicles.queries";
import type { Customer } from "src/services/customerService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { getApiErrorMessage } from "src/util/apiError";
import {
  canSelectMoveDistributor,
  getCustomerDistributorId,
} from "./vehicleMove.helpers";

interface MoveVehiclesModalProps {
  open: boolean;
  currentCustomerId: string | number;
  currentCustomer?: Customer | null;
  vehicleIds: Array<string | number>;
  onCancel: () => void;
  onSuccess: () => void;
}

interface SelectOption {
  label: string;
  value: string | number;
}

const getRecords = (value: unknown): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is Record<string, unknown> =>
        !!item && typeof item === "object",
    );
  }

  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  return getRecords(
    record.response ??
      record.clients ??
      record.customers ??
      record.items ??
      record.content ??
      record.data,
  );
};

const getRecordId = (record: Record<string, unknown>) => {
  const value = record.id ?? record.value ?? record.clientId ?? record.customerId;
  return typeof value === "string" || typeof value === "number"
    ? value
    : undefined;
};

const getCustomerLabel = (record: Record<string, unknown>) => {
  const fullName = [record.name, record.surname]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ");
  const value =
    record.companyName ?? record.label ?? record.title ?? fullName ?? record.name;
  return value === undefined || value === null ? "" : String(value);
};

const toNumber = (value: string | number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const MoveVehiclesModal = ({
  open,
  currentCustomerId,
  currentCustomer,
  vehicleIds,
  onCancel,
  onSuccess,
}: MoveVehiclesModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const canSelectDistributor = canSelectMoveDistributor();
  const selectedTopDistributor = useSelectDistributor(
    (state) => state.selectedDistributor,
  );
  const [targetDistributorId, setTargetDistributorId] = useState<
    string | number | undefined
  >();
  const [targetCustomerId, setTargetCustomerId] = useState<
    string | number | undefined
  >();
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const currentDistributorId = getCustomerDistributorId(currentCustomer);
  const customerDetailQuery = useQueryGetCustomerById(currentCustomerId, {
    enabled: open && !canSelectDistributor && !currentDistributorId,
  });
  const queriedCustomer = customerDetailQuery.data?.data?.response?.client;
  const scopedDistributorId =
    currentDistributorId ?? getCustomerDistributorId(queriedCustomer);

  useEffect(() => {
    if (!open) return;

    setTargetDistributorId(
      canSelectDistributor
        ? (selectedTopDistributor ?? undefined)
        : scopedDistributorId,
    );
    setTargetCustomerId(undefined);
    setConfirmationOpen(false);
  }, [
    canSelectDistributor,
    open,
    scopedDistributorId,
    selectedTopDistributor,
  ]);

  const distributorQuery = useQueryGetDistributorList({
    enabled: open && canSelectDistributor,
  });
  const customerQuery = useQueryGetClientLookup(
    targetDistributorId ? { distributorId: targetDistributorId } : {},
    { enabled: open && !!targetDistributorId },
  );
  const moveMutation = useMutationMoveVehicles();

  const distributorOptions = useMemo<SelectOption[]>(
    () =>
      getRecords(
        (distributorQuery.data as { data?: unknown } | undefined)?.data,
      )
        .map((record) => {
          const value = getRecordId(record);
          const label = String(record.name ?? record.label ?? "");
          return value === undefined || !label ? null : { value, label };
        })
        .filter((option): option is SelectOption => !!option),
    [distributorQuery.data],
  );

  const customerOptions = useMemo<SelectOption[]>(
    () =>
      getRecords((customerQuery.data as { data?: unknown } | undefined)?.data)
        .map((record) => {
          const value = getRecordId(record);
          const label = getCustomerLabel(record);
          return value === undefined || !label ? null : { value, label };
        })
        .filter(
          (option): option is SelectOption =>
            !!option && String(option.value) !== String(currentCustomerId),
        ),
    [currentCustomerId, customerQuery.data],
  );

  const numericVehicleIds = vehicleIds
    .map(toNumber)
    .filter((id): id is number => id !== undefined);
  const numericCurrentCustomerId = toNumber(currentCustomerId);
  const numericTargetCustomerId = targetCustomerId
    ? toNumber(targetCustomerId)
    : undefined;
  const canSave =
    numericVehicleIds.length === vehicleIds.length &&
    numericVehicleIds.length > 0 &&
    !!numericCurrentCustomerId &&
    !!numericTargetCustomerId &&
    String(targetCustomerId) !== String(currentCustomerId);

  const handleClose = () => {
    if (moveMutation.isPending) return;
    setConfirmationOpen(false);
    onCancel();
  };

  const handleConfirm = async () => {
    if (!canSave || !numericCurrentCustomerId || !numericTargetCustomerId) return;

    try {
      await moveMutation.mutateAsync({
        currentCustomerId: numericCurrentCustomerId,
        newCustomerId: numericTargetCustomerId,
        vehicleIds: numericVehicleIds,
      });
      message.success(t("vehicles_moved_successfully"));
      setConfirmationOpen(false);
      onSuccess();
    } catch (error) {
      message.error(
        getApiErrorMessage(error) ?? t("failed_to_move_vehicles"),
      );
    }
  };

  return (
    <>
      <CustomModal
        open={open}
        width={594}
        centered
        closable={false}
        footer={null}
        destroyOnHidden
        onCancel={handleClose}
      >
        <div className="px-4 py-3 sm:px-6">
          <CustomTypography.CustomTitle
            level={3}
            className="!mb-8 !text-[26px] !font-bold"
          >
            {t("select_new_customer")}
          </CustomTypography.CustomTitle>

          <div className="flex flex-col gap-6">
            {canSelectDistributor && (
              <CustomSelect
                label={t("distributor")}
                placeholder={t("select_distributor")}
                value={targetDistributorId}
                options={distributorOptions}
                loading={distributorQuery.isLoading}
                showSearch
                optionFilterProp="label"
                onChange={(value) => {
                  setTargetDistributorId(value);
                  setTargetCustomerId(undefined);
                }}
              />
            )}

            <CustomSelect
              label={t("customer")}
              placeholder={t("select_customer")}
              value={targetCustomerId}
              options={customerOptions}
              loading={customerQuery.isLoading || customerDetailQuery.isLoading}
              disabled={!targetDistributorId}
              showSearch
              optionFilterProp="label"
              onChange={setTargetCustomerId}
            />
          </div>

          <div className="mt-12 grid grid-cols-2 gap-3">
            <CustomButton
              type="secondary"
              label={t("cancel")}
              buttonSize="big"
              className="w-full"
              onClick={handleClose}
            />
            <CustomButton
              type="primary"
              label={t("save")}
              buttonSize="big"
              className="w-full !text-white"
              disabled={!canSave}
              onClick={() => setConfirmationOpen(true)}
            />
          </div>
        </div>
      </CustomModal>

      <CustomSharedConfirmationModal
        open={confirmationOpen}
        onCancel={() => setConfirmationOpen(false)}
        onConfirm={handleConfirm}
        title={t("confirm_vehicle_move")}
        description={t("confirm_vehicle_move_description", {
          count: vehicleIds.length,
        })}
        confirmText={t("confirm")}
        cancelText={t("cancel")}
        isLoading={moveMutation.isPending}
        danger={false}
        centered
      />
    </>
  );
};

export default MoveVehiclesModal;
